import { Component, CSSProperties } from 'react'
import * as cn from 'classnames'
import * as styles from 'components/features/pfm/Highlights/Highlights.styl'
import { Insight as InsightType } from 'interfaces/insights'
import Carousel from 'components/molecules/Carousel'
import { insightShownActionCreator } from 'actions/insightsActions'
import { createFakeInsight } from 'helpers/insightHelpers'
import { NO_HIGHLIGHTS_TEXT } from 'constants/opportunities'
import { ViewportMatchName } from 'interfaces/viewport'
import Insight from 'components/features/insights/Insight/Insight'
import { WithMobileProps } from 'hocs/withMobile/withMobile'
import withPerformanceMetrics from 'hocs/withPerformanceMetrics/withPerformanceMetrics'
import ModuleWrapper2020 from 'components/features/pfm/ModuleWrapper2020/ModuleWrapper2020'

const EMPTY_STATE = createFakeInsight<InsightType>({
  placement: 'dashboard_opportunities',
  type: 'zero_state',
  title: 'You’re all set!',
  text: NO_HIGHLIGHTS_TEXT,
  tags: [],
})

const CARDS_PER_SLIDE_BY_VIEWPORT: Record<ViewportMatchName, number> = {
  w320: 1,
  w375: 1,
  w415: 1,
  w768: 2,
  w1024: 2,
  w1280: 2,
  w1440: 3,
  w1920: 4,
  'not-defined': 3,
}

function normalizeViewport (viewport: ViewportMatchName | null): ViewportMatchName {
  return viewport ?? 'w1920'
}

function getCardsPerSlide (viewport: ViewportMatchName | null): number {
  const normalized = normalizeViewport(viewport)
  return CARDS_PER_SLIDE_BY_VIEWPORT[normalized]
}

function getPageCount (insightCount: number, viewport: ViewportMatchName | null): number {
  if (insightCount <= 0) {
    return 0
  }
  const perSlide = getCardsPerSlide(viewport)
  return Math.ceil(insightCount / perSlide)
}

function chunkInsights<T> (items: T[], size: number): T[][] {
  const result: T[][] = []
  for (let i = 0; i < items.length; i += size) {
    result.push(items.slice(i, i + size))
  }
  return result
}

const REFINE_INDEX = (nextActiveIndex: number, activeIndex: number, isSwipe: boolean) => {
  if (!isSwipe) {
    // Index is changed, because user clicked on the button
    return nextActiveIndex
  }
  // Index is changed at most 1 position, because user performed swipe
  return Math.max(Math.min(nextActiveIndex, activeIndex + 1), activeIndex - 1)
}

const NO_REFINE_INDEX = (nextActiveIndex: number) => nextActiveIndex

export type HighlightsStateProps = {
  viewport: ViewportMatchName | null
  insights: InsightType[]
  dismissedInsights: InsightType[]
}

export type HighlightsDispatchProps = {
  onInsightShown: typeof insightShownActionCreator
  undismiss: (dismissedInsights: InsightType[]) => void
}

type HighlightsProps =
  & HighlightsStateProps
  & HighlightsDispatchProps
  & WithMobileProps

type HighlightsState = {
  carouselActiveIndex: number
  insightCount: number
  viewport: ViewportMatchName | null
  pageCount: number
}

class Highlights extends Component<HighlightsProps, HighlightsState> {
  static getDerivedStateFromProps (nextProps: HighlightsProps, prevState: HighlightsState) {
    const nextInsightCount = nextProps.insights.length
    const nextViewport = nextProps.viewport
    const nextPageCount = getPageCount(nextInsightCount, nextViewport)

    if (
      nextInsightCount === prevState.insightCount
      && nextViewport === prevState.viewport
      && nextPageCount === prevState.pageCount
    ) {
      return prevState
    }

    const maxIndex = Math.max(0, nextPageCount - 1)
    const carouselActiveIndex = Math.min(prevState.carouselActiveIndex, maxIndex)
    return {
      carouselActiveIndex,
      insightCount: nextInsightCount,
      viewport: nextViewport,
      pageCount: nextPageCount,
    }
  }

  state = {
    insightCount: this.props.insights.length,
    carouselActiveIndex: 0,
    viewport: this.props.viewport,
    pageCount: getPageCount(this.props.insights.length, this.props.viewport),
  }

  private getTitle () {
    const { placement } = this.props.insights[0] ?? {}
    switch (placement) {
      case 'future_opportunities': {
        return 'Insights'
      }

      case 'coaching_carousel': {
        return 'Recent Coach Reviews'
      }
    }
    return 'Highlights'
  }

  render () {
    const { insights } = this.props
    const hasHighlights = insights.length > 0
    const highlightsModule = hasHighlights ? (
      <ModuleWrapper2020
        title={ this.getTitle() }
        card={ false }
        noPadding
      >
        <div className={ styles.highlights }>
          <div className={ styles.carouselWrapper }>
            { this.renderCarousel() }
          </div>
        </div>
      </ModuleWrapper2020>
    ) : null


    // Render placeholder to facilitate collapsing animation
    return (
      <>
        { highlightsModule }
        <div className={ cn(styles.emptyPlaceholder, { [styles.collapsed]: !hasHighlights }) } />
      </>
    )
  }

  renderCarousel () {
    const { insights, viewport, isMobile } = this.props
    const cardsPerSlide = getCardsPerSlide(viewport)

    // NOTE:
    // `react-swipeable-views` assumes each slide is 100% width (translation is in "pages").
    // Using variable-width slides (e.g. 47% for iPad widths) causes blank pages at mid breakpoints.
    // So we keep slide width at 100% and render multiple cards inside each slide.
    const slideStyle: CSSProperties = { width: '100%', overflow: 'visible' }
    const containerStyle: CSSProperties = { overflowX: 'visible' }
    const highlights = insights.length ? insights : [EMPTY_STATE]
    const pages = chunkInsights(highlights, cardsPerSlide)

    return (
      <Carousel
        isFinite={ !isMobile }
        refineIndex={ isMobile ? REFINE_INDEX : NO_REFINE_INDEX }
        minSlideCount={ 1 }
        activeIndex={ this.state.carouselActiveIndex }
        className={ styles.carousel }
        slideStyle={ slideStyle }
        containerStyle={ containerStyle }
        onIndexChange={ this.handleIndexChange }
      >
        { pages.map((page) => this.renderPage(page)) }
      </Carousel>
    )
  }

  renderPage (page: InsightType[]) {
    const key = page.map(({ id }) => id).join('|')

    return (
      <div className={ styles.page } key={ key }>
        { page.map((insight, index) => this.renderInsightWrapper(insight, page.length <= 1, index === page.length - 1)) }
      </div>
    )
  }

  renderInsightWrapper (insight: InsightType, isOnlyChild: boolean, isLastInPage: boolean) {
    const className = cn(styles.insightWrapper, {
      [styles.onlyChild]: isOnlyChild,
      [styles.lastInPage]: isLastInPage,
    })

    return (
      <div className={ className } key={ insight.id }>
        { this.renderInsight(insight) }
      </div>
    )
  }

  renderInsight (insight: InsightType) {
    const { onInsightShown } = this.props

    return (
      <Insight
        insight={ insight }
        onShown={ onInsightShown }
      />
    )
  }

  private handleIndexChange = (carouselActiveIndex: number) => {
    this.setState({ carouselActiveIndex })
  }
}

const HighlightsWithMetrics = withPerformanceMetrics(Highlights, 'highlights')

export default HighlightsWithMetrics
